import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { getGa4MeasurementId } from "@/lib/analytics";

/**
 * GA4 single-page-app pageview tracking.
 *
 * The inline gtag `config` in index.html fires a page_view for the initial
 * (or deep-linked) landing only. React Router client-side navigations never
 * re-run `config`, so without this GA4 records nothing past the entry page —
 * the whole in-site funnel is invisible in GA4. (LinkedInTracking already does
 * the equivalent for the LinkedIn Insight Tag; GA4 was simply never wired.)
 *
 * We send an explicit `page_view` on every route change AFTER the first. The
 * first render is skipped so the landing pageview already sent by `config` is
 * not double-counted. Enhanced Measurement "page changes based on browser
 * history events" is OFF for these properties (verified: SPA navigations
 * produced zero /g/collect hits), so gtag's own history listener does not
 * fire a competing page_view.
 *
 * `send_to` is REQUIRED here, and keep it even though the reason it was added
 * has since been removed. A `gtag('event', …)` with no `send_to` broadcasts to
 * every initialized Google tag on the page. When this was written the site
 * also loaded GTM container GTM-5L43GMH4, which initialized its own GA4
 * configs and forwarded a nav page_view of its own, so an unscoped send
 * double-counted.
 *
 * GTM was retired from this site on 2026-08-06 (jw): the container was emptied
 * and published as v5 with zero tags, and the loader was removed from
 * index.html in the same change. The gtag `config` is now the only Google tag
 * on the page, so today an unscoped send would reach exactly one destination
 * and behave identically.
 *
 * It stays pinned anyway. `send_to` is cheap, and it is the difference between
 * "correct" and "correct as long as nobody adds a second destination" — the
 * next tag someone adds silently starts receiving every nav page_view. Pinning
 * makes this component the explicit sole owner of that property's nav
 * page_view rather than the incidental one.
 *
 * Safe before gtag.js loads: the synchronous stub in index.html defines
 * `window.gtag`, so early calls queue and replay in order.
 */
export const GA4Tracking = () => {
  const location = useLocation();
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (typeof window === "undefined") return;

    // Landing pageview is already sent by the inline gtag `config`.
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    try {
      window.gtag?.("event", "page_view", {
        send_to: getGa4MeasurementId(),
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    } catch (error) {
      console.error("GA4 pageview tracking error:", error);
    }
  }, [location.pathname, location.search]);

  return null;
};
